import React, { FunctionComponent, useContext, useEffect } from 'react';

import {
  Alert,
  AlertTitle,
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close, ArrowDropDown } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import * as API from '../../services/API';
import { Scenario } from '../../types/models';

import APICacheContext from '../shared/APICacheContext';
import useSetState from '../../hooks/useSetState';
import APICache from '../../services/APICache';
import PopupDialog from '../shared/PopupDialog';

interface Props {
  scenario: Scenario;
  isOpen: boolean;
  onCancel(): void;
  onComplete(): void;
  clientId?: number;
  availableTags: ScenarioTagOption[];
}

interface ScenarioTagOption {
  id: number;
  name: string;
}

interface State {
  busy: boolean;
  saveLoading: boolean;
  selectedTagId: number | null;
  originalTagId: number | null;
  error?: Error;
}

const INITIAL_STATE: State = {
  busy: false,
  saveLoading: false,
  selectedTagId: null,
  originalTagId: null,
  error: undefined,
};

const ManageScenarioTagDialog: FunctionComponent<Props> = ({
  scenario,
  onComplete,
  isOpen,
  onCancel,
  availableTags,
}) => {
  const manageScenarioTagDialogContext = useContext(APICacheContext);
  const [state, setState] = useSetState<State>(INITIAL_STATE);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isOpen) {
      // Prepopulate from scenario prop if tag exists
      const tagId = scenario.ScenarioTag?.id ?? null;
      setState({
        selectedTagId: tagId,
        originalTagId: tagId,
      });
    }
  }, [isOpen]);

  const handleTagChange = (event: SelectChangeEvent<number>) => {
    setState({
      selectedTagId: event.target.value as number,
    });
  };

  const handleClearTag = () => {
    setState({
      selectedTagId: null,
    });
  };

  const handleReset = () => {
    setState({
      selectedTagId: state.originalTagId,
      error: undefined,
    });
  };

  const handleCancel = () => {
    onCancel();
    setState(INITIAL_STATE);
  };

  const handleSubmit = async () => {
    setState({ error: undefined, saveLoading: true, busy: true });

    // Validate required IDs
    if (!scenario.ModelInstanceID || !scenario.id) {
      setState({ 
        saveLoading: false,
        busy: false, 
        error: new Error('Missing ModelInstanceID or Scenario ID') 
      });
      return;
    }

    try {
      await API.update(
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}/tag_assignment`,
        {
          tag_id: state.selectedTagId,
        }
      );
    } catch (error: any) {
      setState({ saveLoading: false, busy: false, error });
      return;
    }

    // Close dialog immediately after successful API call
    setState({ saveLoading: false, busy: false });
    enqueueSnackbar('Scenario tag assigned successfully', { variant: 'success' });
    onComplete();

    // Reload cache in background without blocking the dialog close
    (manageScenarioTagDialogContext as APICache).load(
      [
        `/instances/${scenario.ModelInstanceID}/scenarios`,
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}`,
      ],
      true
    );
  };

  const { busy, saveLoading, selectedTagId, error } = state;

  return (
    <PopupDialog
      open={isOpen}
      close={handleCancel}
      title="Assign Scenario Tag"
      closeButtonLabel="Cancel"
      primaryButtons={[
        {
          id: 'manage-scenario-tag-dialog-reset',
          label: 'Reset',
          onClick: handleReset,
          disabled: busy,
          variant: 'outlined',
          loading: false,
        },
        {
          id: 'manage-scenario-tag-dialog-save',
          label: 'Save',
          onClick: handleSubmit,
          disabled: saveLoading,
          loading: saveLoading,
        },
      ]}
    >
      <Typography mb={2}>Assign the tag for this scenario.</Typography>

      <FormControl fullWidth size="small">
        <InputLabel id="scenario-tag-label">Scenario Tag</InputLabel>
        <Select
          labelId="scenario-tag-label"
          id="scenario-tag-select"
          value={selectedTagId ?? ''}
          label="Scenario Tag"
          onChange={handleTagChange}
          disabled={busy}
          IconComponent={ArrowDropDown}
          endAdornment={
            selectedTagId ? (
              <InputAdornment position="end" sx={{ position: 'absolute', right: 32 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearTag();
                  }}
                  disabled={busy}
                  edge="end"
                  sx={{ padding: '4px' }}
                  title="Clear selection"
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          sx={{
            '& .MuiSelect-select': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxWidth: '400px',
                '& .MuiList-root': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              },
            },
          }}
        >
          {availableTags.map((tag) => (
            <MenuItem 
              key={tag.id} 
              value={tag.id}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                paddingLeft: '16px !important',
                paddingRight: '16px !important',
              }}
            >
              <Tooltip title={tag.name} placement="right" arrow>
                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {tag.name}
                </span>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {error && (
        <Box mt={2}>
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            Unable to save, check your connection and try again.
          </Alert>
        </Box>
      )}
    </PopupDialog>
  );
};

export default ManageScenarioTagDialog;
